import React from "react";
import { Box, List, ListItem, ListItemIcon, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useCurrentBrand } from "../../../hooks/useCurrentWorkspace";
import { SquareArrowOutUpRight } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2.5),
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },
  statusBox: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    marginBottom: 4,
  },
  status: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.common.black,
  },
  link: {
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    fontSize: 18,
    display: "flex",
    alignItems: "center",

    "&:hover": {
      color: theme.palette.primary.light,
    },
  },

  list: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    marginBottom: theme.spacing(4.5),
    marginTop: theme.spacing(3),
  },

  listItem: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing(3.5),
    },
    [theme.breakpoints.up("md")]: {
      "&:not(:last-child)": {
        marginBottom: theme.spacing(3.5),
      },
    },
  },

  check: {
    marginRight: theme.spacing(1.25),

    "& svg": {
      width: 30,
      height: 30,
      color: theme.palette.primary.main,
    },
  },
}));

const appStoreDocUrl =
  "https://proga-tech.notion.site/Add-Stridist-team-to-App-Store-218a1c4ac8fb804aa006d6085c6a3987?source=copy_link";
const googlePlayStoreDocUrl =
  "https://proga-tech.notion.site/Add-Stridist-team-to-Google-Play-218a1c4ac8fb80e7ab37c024a79d2853?source=copy_link";

interface PendingDevReviewContentProps {}

export function PendingDevReviewContent() {
  const s = useStyles();
  const { brandName } = useCurrentBrand();

  return (
    <Box className={s.header}>
      <Box className={s.titleBox}>
        <Typography className={s.title}>{brandName} Branded App</Typography>
        <Box className={s.statusBox}>
          <Typography className={s.status}>pending dev review</Typography>
        </Box>
      </Box>
      <Typography className={s.text}>
        Your request is under review. We will get back to you shortly with any
        updates or further information.
      </Typography>
      <Typography className={s.text} sx={{ marginTop: 2 }}>
        To proceed with publishing your branded app, please follow linked
        instructions to provide access to your:
      </Typography>
      <List className={s.list}>
        <ListItem className={s.listItem}>
          <a
            href={appStoreDocUrl}
            className={s.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon className={s.check}>
              <SquareArrowOutUpRight size={15} />
            </ListItemIcon>
            App Store
          </a>{" "}
        </ListItem>
        <ListItem className={s.listItem}>
          <a
            href={googlePlayStoreDocUrl}
            className={s.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon className={s.check}>
              <SquareArrowOutUpRight size={15} />
            </ListItemIcon>
            Google Play Store
          </a>{" "}
        </ListItem>
      </List>
      <Typography className={s.text} sx={{ marginTop: 2 }}>
        This will allow us to submit your app to both stores on your behalf.
      </Typography>
      <Typography className={s.text} sx={{ marginTop: 2 }}>
        In the meantime, you can continue using the standard Stridist app for
        your clients.
      </Typography>
    </Box>
  );
}
